// src/constants.js
var MAGIC = [86, 79, 88, 32];
var VERSION = 150;

// src/voxel-grid.js
var VoxelGrid = class {
  constructor(sx, sy, sz) {
    this.sx = sx;
    this.sy = sy;
    this.sz = sz;
    this.voxels = /* @__PURE__ */ new Map();
  }
  _key(x, y, z) {
    return `${x},${y},${z}`;
  }
  set(x, y, z, ci) {
    if (x < 0 || y < 0 || z < 0 || x >= this.sx || y >= this.sy || z >= this.sz) {
      throw new RangeError(`voxel (${x},${y},${z}) \u8D85\u51FA\u8FB9\u754C ${this.sx}x${this.sy}x${this.sz}`);
    }
    if (ci < 0 || ci > 255) throw new RangeError("\u989C\u8272\u7D22\u5F15\u5FC5\u987B 0..255");
    this.voxels.set(this._key(x, y, z), ci);
  }
  addSphere(cx, cy, cz, r, ciFn) {
    const r2 = r * r;
    for (let x = Math.max(0, Math.floor(cx - r)); x < Math.min(this.sx, Math.ceil(cx + r)); x++) {
      for (let y = Math.max(0, Math.floor(cy - r)); y < Math.min(this.sy, Math.ceil(cy + r)); y++) {
        for (let z = Math.max(0, Math.floor(cz - r)); z < Math.min(this.sz, Math.ceil(cz + r)); z++) {
          const dx = x - cx, dy = y - cy, dz = z - cz;
          if (dx * dx + dy * dy + dz * dz <= r2) {
            this.set(x, y, z, ciFn(dx, dy, dz, Math.sqrt(dx * dx + dy * dy + dz * dz)));
          }
        }
      }
    }
  }
  get length() {
    return this.voxels.size;
  }
  // 返回有序数组 [{x,y,z,i}]
  list() {
    const out = [];
    for (const [key, i] of this.voxels) {
      const [x, y, z] = key.split(",").map(Number);
      out.push({ x, y, z, i });
    }
    out.sort((a, b) => a.x - b.x || a.y - b.y || a.z - b.z || a.i - b.i);
    return out;
  }
};

// src/writer.js
function str4(s) {
  const b = new Uint8Array(4);
  for (let i = 0; i < 4; i++) b[i] = s.charCodeAt(i) & 255;
  return b;
}
function concat(...arrays) {
  let len = 0;
  for (const a of arrays) len += a.length;
  const out = new Uint8Array(len);
  let off = 0;
  for (const a of arrays) {
    out.set(a, off);
    off += a.length;
  }
  return out;
}
function chunk(id, content, M = 0) {
  const head = new Uint8Array(12);
  const dv = new DataView(head.buffer);
  head.set(str4(id), 0);
  dv.setUint32(4, content.length, true);
  dv.setUint32(8, M, true);
  return concat(head, content);
}
function sizeChunk(sx, sy, sz) {
  const c = new Uint8Array(12);
  const dv = new DataView(c.buffer);
  dv.setUint32(0, sx, true);
  dv.setUint32(4, sy, true);
  dv.setUint32(8, sz, true);
  return chunk("SIZE", c);
}
function xyziChunk(voxels) {
  const c = new Uint8Array(4 + voxels.length * 4);
  const dv = new DataView(c.buffer);
  dv.setUint32(0, voxels.length, true);
  let off = 4;
  for (const v of voxels) {
    c[off] = v.x;
    c[off + 1] = v.y;
    c[off + 2] = v.z;
    c[off + 3] = v.i;
    off += 4;
  }
  return chunk("XYZI", c);
}
function rgbaChunk(palette) {
  const c = new Uint8Array(256 * 4);
  let off = 0;
  for (let i = 0; i < 255; i++) {
    const [r2, g2, b2, a2] = palette[i + 1];
    c[off++] = r2;
    c[off++] = g2;
    c[off++] = b2;
    c[off++] = a2;
  }
  const [r, g, b, a] = palette[0];
  c[off++] = r;
  c[off++] = g;
  c[off++] = b;
  c[off++] = a;
  return chunk("RGBA", c);
}
function dictToBytes(dict) {
  const keys = Object.keys(dict);
  const parts = [];
  const head = new Uint8Array(4);
  new DataView(head.buffer).setUint32(0, keys.length, true);
  parts.push(head);
  for (const k of keys) {
    const raw = dict[k];
    let vb;
    if (raw && typeof raw === "object" && !Array.isArray(raw)) {
      vb = dictToBytes(raw);
    } else {
      vb = new TextEncoder().encode(String(raw));
    }
    const kb = new TextEncoder().encode(k);
    const kh = new Uint8Array(4);
    new DataView(kh.buffer).setUint32(0, kb.length, true);
    const vh = new Uint8Array(4);
    new DataView(vh.buffer).setUint32(0, vb.length, true);
    parts.push(kh, kb, vh, vb);
  }
  return concat(...parts);
}
function matlChunk(id, mat) {
  const dict = {};
  dict._type = mat.type || "_diffuse";
  if (mat.metalness !== void 0) dict._metal = String(mat.metalness);
  if (mat.roughness !== void 0) dict._rough = String(mat.roughness);
  if (mat.alpha !== void 0) dict._alpha = String(mat.alpha);
  if (mat.emissive !== void 0) dict._emit = String(mat.emissive);
  if (mat.ior !== void 0) dict._ior = String(mat.ior);
  if (mat.flux !== void 0) dict._flux = String(mat.flux);
  if (mat.density !== void 0) dict._d = String(mat.density);
  if (mat.specular !== void 0) dict._sp = String(mat.specular);
  if (mat.glow !== void 0) dict._g = String(mat.glow);
  const c = new Uint8Array(4);
  new DataView(c.buffer).setUint32(0, id, true);
  return chunk("MATL", concat(c, dictToBytes(dict)));
}
function nTRNChunk(nodeId, childId, { name = "", translation = [0, 0, 0], rotation = 0, hidden = false, keyframes = null }) {
  const nodeDict = {};
  if (name) nodeDict._name = name;
  if (hidden) nodeDict._hidden = "1";
  const trnDict = { _t: translation.join(" "), _r: String(rotation) };
  if (keyframes && keyframes.length > 1) {
    const fc = keyframes.length;
    const ts = keyframes.map((kf) => (kf.translation || [0, 0, 0]).join(" ")).join(" ");
    const rs = keyframes.map((kf) => String(kf.rotation || 0)).join(" ");
    const ps = keyframes.map(() => "0 0 0").join(" ");
    trnDict._f = { _f: String(fc), _t: ts, _r: rs, _p: ps };
  }
  const cId = new Uint8Array(4);
  new DataView(cId.buffer).setUint32(0, nodeId, true);
  const cChild = new Uint8Array(4);
  new DataView(cChild.buffer).setUint32(0, childId, true);
  const reserved = new Uint8Array(4);
  return chunk("nTRN", concat(cId, dictToBytes(nodeDict), cChild, reserved, dictToBytes(trnDict)));
}
function nGRPChunk(nodeId, children) {
  const cId = new Uint8Array(4);
  new DataView(cId.buffer).setUint32(0, nodeId, true);
  const cNum = new Uint8Array(4);
  new DataView(cNum.buffer).setUint32(0, children.length, true);
  const cChildren = new Uint8Array(children.length * 4);
  const dv = new DataView(cChildren.buffer);
  for (let i = 0; i < children.length; i++) dv.setUint32(i * 4, children[i], true);
  return chunk("nGRP", concat(cId, dictToBytes({}), cNum, cChildren));
}
function nSHPChunk(nodeId, modelId) {
  const cId = new Uint8Array(4);
  new DataView(cId.buffer).setUint32(0, nodeId, true);
  const cModel = new Uint8Array(4);
  new DataView(cModel.buffer).setUint32(0, modelId, true);
  return chunk("nSHP", concat(cId, dictToBytes({}), cModel, dictToBytes({ _t: "0 0 0" })));
}
function toVoxBytes(grid, palette = null) {
  const voxels = grid.list();
  let children = concat(sizeChunk(grid.sx, grid.sy, grid.sz), xyziChunk(voxels));
  if (palette) {
    if (palette.length !== 256) throw new Error("palette \u5FC5\u987B\u6709 256 \u9879");
    children = concat(children, rgbaChunk(palette));
  }
  const main = chunk("MAIN", new Uint8Array(0), children.length);
  const header = new Uint8Array(8);
  header.set(MAGIC, 0);
  new DataView(header.buffer).setUint32(4, VERSION, true);
  return concat(header, main, children);
}
function toVoxBytesScene(data, palette = null) {
  const models = data.models || [];
  const scene = data.scene && data.scene.length ? data.scene : models.map((_, i) => ({ modelIndex: i, translation: [0, 0, 0], rotation: 0, hidden: false, name: "" }));
  const materials = data.materials || {};
  const frameCount = data.frameCount && data.frameCount > 1 ? data.frameCount : scene.reduce((m, inst) => Math.max(m, inst.frames ? inst.frames.length : 1), 1);
  let children = new Uint8Array(0);
  for (const m of models) {
    children = concat(children, sizeChunk(m.size[0], m.size[1], m.size[2]), xyziChunk(m.voxels));
  }
  if (palette) {
    if (palette.length !== 256) throw new Error("palette \u5FC5\u987B\u6709 256 \u9879");
    children = concat(children, rgbaChunk(palette));
  }
  for (const id in materials) children = concat(children, matlChunk(Number(id), materials[id]));
  if (frameCount > 1) {
    const fc = new Uint8Array(4);
    new DataView(fc.buffer).setUint32(0, frameCount, true);
    children = concat(children, chunk("FRAM", dictToBytes({ _f: String(frameCount) })));
  }
  const N = scene.length;
  const groupId = 0;
  const trnIds = [];
  const shpIds = [];
  for (let j = 0; j < N; j++) {
    trnIds.push(1 + j);
    shpIds.push(1 + N + j);
  }
  children = concat(children, nGRPChunk(groupId, trnIds));
  for (let j = 0; j < N; j++) {
    const inst = scene[j];
    children = concat(
      children,
      nTRNChunk(trnIds[j], shpIds[j], {
        name: inst.name || "",
        translation: inst.translation || [0, 0, 0],
        rotation: inst.rotation || 0,
        hidden: !!inst.hidden,
        keyframes: inst.frames && inst.frames.length > 1 ? inst.frames : null
      }),
      nSHPChunk(shpIds[j], inst.modelIndex)
    );
  }
  const main = chunk("MAIN", new Uint8Array(0), children.length);
  const header = new Uint8Array(8);
  header.set(MAGIC, 0);
  new DataView(header.buffer).setUint32(4, VERSION, true);
  return concat(header, main, children);
}
function downloadVox(grid, filename = "model.vox", palette = null) {
  const bytes = toVoxBytes(grid, palette);
  const blob = new Blob([bytes], { type: "application/octet-stream" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// src/rotation.ts
var ROTATION_MATRICES = (() => {
  const axes = [
    [1, 0, 0],
    [-1, 0, 0],
    [0, 1, 0],
    [0, -1, 0],
    [0, 0, 1],
    [0, 0, -1]
  ];
  const out = [];
  const seen = /* @__PURE__ */ new Set();
  for (const x of axes) {
    for (const y of axes) {
      if (x[0] * y[0] + x[1] * y[1] + x[2] * y[2] !== 0) continue;
      const z = [
        x[1] * y[2] - x[2] * y[1],
        x[2] * y[0] - x[0] * y[2],
        x[0] * y[1] - x[1] * y[0]
      ];
      const m = [x[0], x[1], x[2], y[0], y[1], y[2], z[0], z[1], z[2]];
      const k = m.join(",");
      if (!seen.has(k)) {
        seen.add(k);
        out.push(m);
      }
    }
  }
  return out;
})();
function matMul3(a, b) {
  const r = new Array(9);
  for (let i = 0; i < 3; i++) {
    for (let j = 0; j < 3; j++) {
      let s = 0;
      for (let k = 0; k < 3; k++) s += a[i * 3 + k] * b[k * 3 + j];
      r[i * 3 + j] = s;
    }
  }
  return r;
}
function matVec3(m, v) {
  return [
    m[0] * v[0] + m[1] * v[1] + m[2] * v[2],
    m[3] * v[0] + m[4] * v[1] + m[5] * v[2],
    m[6] * v[0] + m[7] * v[1] + m[8] * v[2]
  ];
}
function rotationIndex(m) {
  const target = m.map((n) => Math.round(n)).join(",");
  for (let i = 0; i < ROTATION_MATRICES.length; i++) {
    if (ROTATION_MATRICES[i].join(",") === target) return i;
  }
  return 0;
}

// src/scene.ts
function buildScene(nodes, modelCount, frameCount) {
  const parents = {};
  for (const id in nodes) {
    const n = nodes[id];
    if (n.type === "group") for (const c of n.children) parents[c] = Number(id);
    else if (n.type === "transform") parents[n.child] = Number(id);
  }
  const instances = [];
  for (const id in nodes) {
    if (nodes[id].type !== "shape") continue;
    const path = [];
    let cur = Number(id);
    while (cur !== void 0) {
      path.push(cur);
      cur = parents[cur];
    }
    path.reverse();
    let hidden = false;
    let name = "";
    let hasAnim = false;
    for (const nid of path) {
      const n = nodes[nid];
      if (!n) continue;
      if (n.type === "transform") {
        if (n.hidden) hidden = true;
        if (n.name) name = n.name;
        if (n.keyframes && n.keyframes.length) hasAnim = true;
      } else if (n.type === "shape") {
        if (n.name) name = n.name;
      } else if (n.type === "group") {
        if (n.name) name = n.name;
      }
    }
    const frames = [];
    for (let f = 0; f < frameCount; f++) {
      let cumR = ROTATION_MATRICES[0];
      let cumT = [0, 0, 0];
      for (const nid of path) {
        const n = nodes[nid];
        if (!n) continue;
        if (n.type === "transform") {
          const kf = n.keyframes && n.keyframes.length ? n.keyframes[Math.min(f, n.keyframes.length - 1)] : { translation: n.translation, rotation: n.rotation, pivot: [0, 0, 0] };
          const p = kf.pivot || [0, 0, 0];
          const R = ROTATION_MATRICES[kf.rotation] || ROTATION_MATRICES[0];
          const rp = matVec3(R, [-p[0], -p[1], -p[2]]);
          const tInc = [p[0] + rp[0], p[1] + rp[1], p[2] + rp[2]];
          const tLocal = [
            tInc[0] + (kf.translation[0] || 0),
            tInc[1] + (kf.translation[1] || 0),
            tInc[2] + (kf.translation[2] || 0)
          ];
          cumT = [
            cumT[0] + matVec3(cumR, tLocal)[0],
            cumT[1] + matVec3(cumR, tLocal)[1],
            cumT[2] + matVec3(cumR, tLocal)[2]
          ];
          cumR = matMul3(cumR, R);
        } else if (n.type === "shape") {
          const off = n.offset || [0, 0, 0];
          cumT = [
            cumT[0] + matVec3(cumR, off)[0],
            cumT[1] + matVec3(cumR, off)[1],
            cumT[2] + matVec3(cumR, off)[2]
          ];
        }
      }
      frames.push({
        translation: cumT.map((v) => Math.round(v)),
        rotation: rotationIndex(cumR)
      });
    }
    instances.push({
      modelIndex: nodes[id].modelId,
      translation: frames[0].translation,
      rotation: frames[0].rotation,
      hidden,
      name,
      // 仅当路径上真的有关键帧节点才附加 frames, 保持静态文件(及动画里的静态对象)无损
      frames: hasAnim ? frames : void 0
    });
  }
  if (instances.length === 0) {
    const count = modelCount || 0;
    for (let i = 0; i < count; i++) {
      instances.push({ modelIndex: i, translation: [0, 0, 0], rotation: 0, hidden: false, name: "" });
    }
  }
  return instances;
}

// src/parse.ts
function ascii4(u8, off) {
  let s = "";
  for (let i = 0; i < 4; i++) s += String.fromCharCode(u8[off + i]);
  return s;
}
function tryParseNestedDict(dv, u8, start, end, depth) {
  if (end - start < 4) return null;
  const n = dv.getUint32(start, true);
  if (n < 0 || n > 1e5) return null;
  let p = start + 4;
  for (let i = 0; i < n; i++) {
    if (p + 8 > end) return null;
    const klen = dv.getUint32(p, true);
    p += 4;
    if (p + klen > end) return null;
    p += klen;
    const vlen = dv.getUint32(p, true);
    p += 4;
    if (p + vlen > end) return null;
    p += vlen;
  }
  if (p !== end) return null;
  const { dict } = readDict(dv, u8, start, end, depth);
  return dict;
}
function readDict(dv, u8, off, end, depth = 0) {
  let p = off;
  const num = dv.getUint32(p, true);
  p += 4;
  const dict = {};
  for (let i = 0; i < num; i++) {
    if (p + 8 > end) break;
    const klen = dv.getUint32(p, true);
    p += 4;
    let key = "";
    for (let j = 0; j < klen; j++) key += String.fromCharCode(u8[p + j]);
    p += klen;
    const vlen = dv.getUint32(p, true);
    p += 4;
    const vstart = p;
    let val;
    if (depth < 4) {
      const nested = tryParseNestedDict(dv, u8, vstart, vstart + vlen, depth + 1);
      if (nested) {
        val = nested;
      } else {
        let s = "";
        for (let j = 0; j < vlen; j++) s += String.fromCharCode(u8[vstart + j]);
        val = s;
      }
    } else {
      let s = "";
      for (let j = 0; j < vlen; j++) s += String.fromCharCode(u8[vstart + j]);
      val = s;
    }
    p += vlen;
    dict[key] = val;
  }
  return { dict, next: p };
}
function readVec3(str) {
  if (!str) return [0, 0, 0];
  const parts = str.trim().split(/\s+/).map(Number);
  return [parts[0] || 0, parts[1] || 0, parts[2] || 0];
}
function parseMaterial(dict) {
  const mat = { type: dict._type || dict.type || "_diffuse" };
  const num = (v) => v === void 0 ? void 0 : parseFloat(v);
  const metalness = num(dict._metal);
  const roughness = num(dict._rough);
  const alpha = dict._alpha !== void 0 ? parseFloat(dict._alpha) : void 0;
  const emissive = num(dict._emit);
  const ior = num(dict._ior);
  const flux = num(dict._flux);
  const density = num(dict._d);
  const specular = num(dict._sp);
  const glow = num(dict._g);
  if (metalness !== void 0) mat.metalness = metalness;
  if (roughness !== void 0) mat.roughness = roughness;
  if (alpha !== void 0) mat.alpha = alpha;
  if (emissive !== void 0) mat.emissive = emissive;
  if (ior !== void 0) mat.ior = ior;
  if (flux !== void 0) mat.flux = flux;
  if (density !== void 0) mat.density = density;
  if (specular !== void 0) mat.specular = specular;
  if (glow !== void 0) mat.glow = glow;
  return mat;
}
function parseVox(input) {
  const u8 = input instanceof Uint8Array ? input : new Uint8Array(input);
  const dv = new DataView(u8.buffer, u8.byteOffset, u8.byteLength);
  if (ascii4(u8, 0) !== "VOX ") throw new Error("\u4E0D\u662F\u5408\u6CD5\u7684 VOX \u6587\u4EF6 (magic \u4E0D\u5339\u914D)");
  const version = dv.getUint32(4, true);
  const models = [];
  let palette = null;
  const nodes = {};
  const materials = {};
  let declaredModels = 0;
  let frameCount = 1;
  function parseChildren(start, end) {
    let p = start;
    while (p + 12 <= end) {
      const id = ascii4(u8, p);
      const N = dv.getUint32(p + 4, true);
      const M2 = dv.getUint32(p + 8, true);
      const contentStart = p + 12;
      const contentEnd = contentStart + N;
      const childStart = contentEnd;
      const childEnd = childStart + M2;
      if (id === "MAIN") {
        parseChildren(childStart, childEnd);
      } else if (id === "PACK") {
        declaredModels = dv.getUint32(contentStart, true);
      } else if (id === "FRAM") {
        const { dict } = readDict(dv, u8, contentStart, contentEnd);
        frameCount = dict._f !== void 0 ? parseInt(dict._f, 10) || 1 : 1;
      } else if (id === "SIZE") {
        const sx = dv.getUint32(contentStart, true);
        const sy = dv.getUint32(contentStart + 4, true);
        const sz = dv.getUint32(contentStart + 8, true);
        models.push({ size: [sx, sy, sz], voxels: [] });
      } else if (id === "XYZI") {
        const n = dv.getUint32(contentStart, true);
        const vox = [];
        let off = contentStart + 4;
        for (let k = 0; k < n; k++) {
          vox.push({ x: u8[off], y: u8[off + 1], z: u8[off + 2], i: u8[off + 3] });
          off += 4;
        }
        if (models.length) models[models.length - 1].voxels = vox;
      } else if (id === "RGBA") {
        const raw = [];
        for (let k = 0; k < 256; k++) {
          const o = contentStart + k * 4;
          raw.push([u8[o], u8[o + 1], u8[o + 2], u8[o + 3]]);
        }
        const logical = Array.from({ length: 256 }, () => [0, 0, 0, 0]);
        for (let i = 0; i < 255; i++) logical[i + 1] = raw[i];
        logical[0] = raw[255];
        palette = logical;
      } else if (id === "MATL") {
        const mid = dv.getUint32(contentStart, true);
        const { dict } = readDict(dv, u8, contentStart + 4, contentEnd);
        materials[mid] = parseMaterial(dict);
      } else if (id === "nTRN") {
        const nodeId = dv.getUint32(contentStart, true);
        const a = readDict(dv, u8, contentStart + 4, contentEnd);
        let q = a.next;
        const childNodeId = dv.getUint32(q, true);
        q += 4;
        q += 4;
        const t = readDict(dv, u8, q, contentEnd);
        const td = t.dict;
        let keyframes = null;
        if (td._f && typeof td._f === "object") {
          const fc = parseInt(td._f._f, 10) || 1;
          const ts = (td._f._t || "").trim().split(/\s+/).filter(Boolean).map(Number);
          const rs = (td._f._r || "").trim().split(/\s+/).filter(Boolean).map(Number);
          const ps = (td._f._p || "").trim().split(/\s+/).filter(Boolean).map(Number);
          keyframes = [];
          for (let k = 0; k < fc; k++) {
            keyframes.push({
              translation: [ts[k * 3] || 0, ts[k * 3 + 1] || 0, ts[k * 3 + 2] || 0],
              rotation: rs[k] || 0,
              pivot: [ps[k * 3] || 0, ps[k * 3 + 1] || 0, ps[k * 3 + 2] || 0]
            });
          }
        }
        nodes[nodeId] = {
          type: "transform",
          child: childNodeId,
          name: a.dict._name || "",
          hidden: a.dict._hidden === "1",
          translation: readVec3(td._t),
          rotation: td._r !== void 0 ? parseInt(td._r, 10) || 0 : 0,
          keyframes
        };
      } else if (id === "nGRP") {
        const nodeId = dv.getUint32(contentStart, true);
        const a = readDict(dv, u8, contentStart + 4, contentEnd);
        let q = a.next;
        const n = dv.getUint32(q, true);
        q += 4;
        const children = [];
        for (let i = 0; i < n; i++) {
          children.push(dv.getUint32(q, true));
          q += 4;
        }
        nodes[nodeId] = { type: "group", children, name: a.dict._name || "" };
      } else if (id === "nSHP") {
        const nodeId = dv.getUint32(contentStart, true);
        const a = readDict(dv, u8, contentStart + 4, contentEnd);
        let q = a.next;
        const modelId = dv.getUint32(q, true);
        q += 4;
        const t = readDict(dv, u8, q, contentEnd);
        nodes[nodeId] = {
          type: "shape",
          modelId,
          name: a.dict._name || "",
          offset: readVec3(t.dict._t)
        };
      }
      p = childEnd;
    }
  }
  if (ascii4(u8, 8) !== "MAIN") throw new Error("\u9876\u5C42 chunk \u4E0D\u662F MAIN");
  const M = dv.getUint32(16, true);
  parseChildren(20, 20 + M);
  for (const id in nodes) {
    const n = nodes[id];
    if (n.type === "transform" && n.keyframes && n.keyframes.length > frameCount) {
      frameCount = n.keyframes.length;
    }
  }
  const scene = buildScene(nodes, models.length, frameCount);
  return { version, models, palette, scene, materials, frameCount };
}

// src/palette.js
var DEFAULT_HEX = [
  0,
  4294967295,
  4291624959,
  4288282623,
  4284940287,
  4281597951,
  4278255615,
  4294954239,
  4291611903,
  4288269567,
  4284927231,
  4281584895,
  4278242559,
  4294941183,
  4291598847,
  4288256511,
  4284914175,
  4281571839,
  4278229503,
  4294928127,
  4291585791,
  4288243455,
  4284901119,
  4281558783,
  4278216447,
  4294915071,
  4291572735,
  4288230399,
  4284888063,
  4281545727,
  4278203391,
  4294902015,
  4291559679,
  4288217343,
  4284875007,
  4281532671,
  4278190335,
  4294967244,
  4291624908,
  4288282572,
  4284940236,
  4281597900,
  4278255564,
  4294954188,
  4291611852,
  4288269516,
  4284927180,
  4281584844,
  4278242508,
  4294941132,
  4291598796,
  4288256460,
  4284914124,
  4281571788,
  4278229452,
  4294928076,
  4291585740,
  4288243404,
  4284901068,
  4281558732,
  4278216396,
  4294915020,
  4291572684,
  4288230348,
  4284888012,
  4281545676,
  4278203340,
  4294901964,
  4291559628,
  4288217292,
  4284874956,
  4281532620,
  4278190284,
  4294967193,
  4291624857,
  4288282521,
  4284940185,
  4281597849,
  4278255513,
  4294954137,
  4291611801,
  4288269465,
  4284927129,
  4281584793,
  4278242457,
  4294941081,
  4291598745,
  4288256409,
  4284914073,
  4281571737,
  4278229401,
  4294928025,
  4291585689,
  4288243353,
  4284901017,
  4281558681,
  4278216345,
  4294914969,
  4291572633,
  4288230297,
  4284887961,
  4281545625,
  4278203289,
  4294901913,
  4291559577,
  4288217241,
  4284874905,
  4281532569,
  4278190233,
  4294967142,
  4291624806,
  4288282470,
  4284940134,
  4281597798,
  4278255462,
  4294954086,
  4291611750,
  4288269414,
  4284927078,
  4281584742,
  4278242406,
  4294941030,
  4291598694,
  4288256358,
  4284914022,
  4281571686,
  4278229350,
  4294927974,
  4291585638,
  4288243302,
  4284900966,
  4281558630,
  4278216294,
  4294914918,
  4291572582,
  4288230246,
  4284887910,
  4281545574,
  4278203238,
  4294901862,
  4291559526,
  4288217190,
  4284874854,
  4281532518,
  4278190182,
  4294967091,
  4291624755,
  4288282419,
  4284940083,
  4281597747,
  4278255411,
  4294954035,
  4291611699,
  4288269363,
  4284927027,
  4281584691,
  4278242355,
  4294940979,
  4291598643,
  4288256307,
  4284913971,
  4281571635,
  4278229299,
  4294927923,
  4291585587,
  4288243251,
  4284900915,
  4281558579,
  4278216243,
  4294914867,
  4291572531,
  4288230195,
  4284887859,
  4281545523,
  4278203187,
  4294901811,
  4291559475,
  4288217139,
  4284874803,
  4281532467,
  4278190131,
  4294967040,
  4291624704,
  4288282368,
  4284940032,
  4281597696,
  4278255360,
  4294953984,
  4291611648,
  4288269312,
  4284926976,
  4281584640,
  4278242304,
  4294940928,
  4291598592,
  4288256256,
  4284913920,
  4281571584,
  4278229248,
  4294927872,
  4291585536,
  4288243200,
  4284900864,
  4281558528,
  4278216192,
  4294914816,
  4291572480,
  4288230144,
  4284887808,
  4281545472,
  4278203136,
  4294901760,
  4291559424,
  4288217088,
  4284874752,
  4281532416,
  4278190318,
  4278190301,
  4278190267,
  4278190250,
  4278190216,
  4278190199,
  4278190165,
  4278190148,
  4278190114,
  4278190097,
  4278251008,
  4278246656,
  4278237952,
  4278233600,
  4278224896,
  4278220544,
  4278211840,
  4278207488,
  4278198784,
  4278194432,
  4293787648,
  4292673536,
  4290445312,
  4289331200,
  4287102976,
  4285988864,
  4283760640,
  4282646528,
  4280418304,
  4279304192,
  4293848814,
  4292730333,
  4290493371,
  4289374890,
  4287137928,
  4286019447,
  4283782485,
  4282664004,
  4280427042,
  4279308561
];
function defaultPalette() {
  return DEFAULT_HEX.map((h) => [h & 255, h >> 8 & 255, h >> 16 & 255, h >> 24 & 255]);
}
function hsvToRgb(h, s, v) {
  h = (h % 1 + 1) % 1;
  const i = Math.floor(h * 6);
  const f = h * 6 - i;
  const p = v * (1 - s);
  const q = v * (1 - f * s);
  const t = v * (1 - (1 - f) * s);
  let r, g, b;
  switch (i % 6) {
    case 0:
      r = v;
      g = t;
      b = p;
      break;
    case 1:
      r = q;
      g = v;
      b = p;
      break;
    case 2:
      r = p;
      g = v;
      b = t;
      break;
    case 3:
      r = p;
      g = q;
      b = v;
      break;
    case 4:
      r = t;
      g = p;
      b = v;
      break;
    case 5:
      r = v;
      g = p;
      b = q;
      break;
  }
  return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
}
function rainbowPalette(baseColor = [130, 130, 140, 255]) {
  const pal = Array.from({ length: 256 }, () => [0, 0, 0, 0]);
  for (let i = 1; i < 255; i++) {
    const frac = (i - 1) / 253;
    const [r, g, b] = hsvToRgb(frac, 0.85, 1);
    pal[i] = [r, g, b, 255];
  }
  pal[255] = baseColor.slice();
  return pal;
}

// src/schematic.js
var airName = "minecraft:air";
async function gzipDeflate(data) {
  if (typeof globalThis.CompressionStream !== "function") {
    throw new Error("\u5F53\u524D\u73AF\u5883\u4E0D\u652F\u6301 CompressionStream, \u65E0\u6CD5\u538B\u7F29 schematic (\u9700 Node18+/\u73B0\u4EE3\u6D4F\u89C8\u5668)");
  }
  const cs = new globalThis.CompressionStream("gzip");
  const stream = new Response(new Blob([data]).stream().pipeThrough(cs)).arrayBuffer();
  return new Uint8Array(await stream);
}
async function gzipInflate(data) {
  if (typeof globalThis.DecompressionStream !== "function") {
    throw new Error("\u5F53\u524D\u73AF\u5883\u4E0D\u652F\u6301 DecompressionStream, \u65E0\u6CD5\u89E3\u538B schematic (\u9700 Node18+/\u73B0\u4EE3\u6D4F\u89C8\u5668)");
  }
  const ds = new globalThis.DecompressionStream("gzip");
  const stream = new Response(new Blob([data]).stream().pipeThrough(ds)).arrayBuffer();
  return new Uint8Array(await stream);
}
var TAG_END = 0;
var TAG_BYTE = 1;
var TAG_SHORT = 2;
var TAG_INT = 3;
var TAG_LONG = 4;
var TAG_FLOAT = 5;
var TAG_DOUBLE = 6;
var TAG_BYTE_ARRAY = 7;
var TAG_STRING = 8;
var TAG_LIST = 9;
var TAG_COMPOUND = 10;
var TAG_INT_ARRAY = 11;
var Reader = class {
  constructor(buf) {
    this.dv = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
    this.u8 = new Uint8Array(buf.buffer, buf.byteOffset, buf.byteLength);
    this.pos = 0;
  }
  byte() {
    return this.u8[this.pos++];
  }
  u16() {
    const v = this.dv.getUint16(this.pos, false);
    this.pos += 2;
    return v;
  }
  i16() {
    const v = this.dv.getInt16(this.pos, false);
    this.pos += 2;
    return v;
  }
  i32() {
    const v = this.dv.getInt32(this.pos, false);
    this.pos += 4;
    return v;
  }
  i64() {
    const hi = this.dv.getInt32(this.pos, false);
    const lo = this.dv.getUint32(this.pos + 4, false);
    this.pos += 8;
    return hi * 4294967296 + lo;
  }
  f32() {
    const v = this.dv.getFloat32(this.pos, false);
    this.pos += 4;
    return v;
  }
  f64() {
    const v = this.dv.getFloat64(this.pos, false);
    this.pos += 8;
    return v;
  }
  bytes(n) {
    const v = this.u8.subarray(this.pos, this.pos + n);
    this.pos += n;
    return v;
  }
  string() {
    const len = this.u16();
    const arr = this.u8.subarray(this.pos, this.pos + len);
    this.pos += len;
    return new TextDecoder().decode(arr);
  }
};
function readNamedTag(r) {
  const type = r.byte();
  if (type === TAG_END) return null;
  const name = r.string();
  return { type, name, value: readTagValue(r, type) };
}
function readTagValue(r, type) {
  switch (type) {
    case TAG_BYTE:
      return r.byte();
    case TAG_SHORT:
      return r.i16();
    case TAG_INT:
      return r.i32();
    case TAG_LONG:
      return r.i64();
    case TAG_FLOAT:
      return r.f32();
    case TAG_DOUBLE:
      return r.f64();
    case TAG_BYTE_ARRAY: {
      const len = r.i32();
      return r.bytes(len);
    }
    case TAG_STRING:
      return r.string();
    case TAG_LIST: {
      const elemType = r.byte();
      const len = r.i32();
      const arr = [];
      for (let i = 0; i < len; i++) arr.push(readTagValue(r, elemType));
      return arr;
    }
    case TAG_COMPOUND: {
      const obj = {};
      for (; ; ) {
        const t = readNamedTag(r);
        if (t === null) break;
        obj[t.name] = t.value;
      }
      return obj;
    }
    case TAG_INT_ARRAY: {
      const len = r.i32();
      const arr = new Array(len);
      for (let i = 0; i < len; i++) arr[i] = r.i32();
      return arr;
    }
    default:
      throw new Error(`NBT: \u4E0D\u652F\u6301\u7684 tag \u7C7B\u578B ${type}`);
  }
}
function parseNbt(bytes) {
  const r = new Reader(bytes);
  const t = readNamedTag(r);
  if (!t || t.type !== TAG_COMPOUND) throw new Error("NBT: \u6839\u8282\u70B9\u4E0D\u662F Compound");
  return t.value;
}
var Writer = class {
  constructor() {
    this.chunks = [];
  }
  byte(v) {
    this.chunks.push(Uint8Array.of(v & 255));
  }
  i16(v) {
    const b = new Uint8Array(2);
    new DataView(b.buffer).setInt16(0, v, false);
    this.chunks.push(b);
  }
  i32(v) {
    const b = new Uint8Array(4);
    new DataView(b.buffer).setInt32(0, v, false);
    this.chunks.push(b);
  }
  u8arr(b) {
    this.chunks.push(b);
  }
  string(s) {
    const enc = new TextEncoder().encode(s);
    this.i16(enc.length);
    this.chunks.push(enc);
  }
  bytes() {
    let len = 0;
    for (const c of this.chunks) len += c.length;
    const out = new Uint8Array(len);
    let off = 0;
    for (const c of this.chunks) {
      out.set(c, off);
      off += c.length;
    }
    return out;
  }
};
function writeNamedTag(w, type, name, value) {
  w.byte(type);
  w.string(name);
  writeTagValue(w, type, value);
}
function writeTagValue(w, type, value) {
  switch (type) {
    case TAG_BYTE:
      w.byte(value);
      break;
    case TAG_SHORT:
      w.i16(value);
      break;
    case TAG_INT:
      w.i32(value);
      break;
    case TAG_STRING:
      w.string(value);
      break;
    case TAG_BYTE_ARRAY:
      w.i32(value.length);
      w.u8arr(value);
      break;
    case TAG_INT_ARRAY:
      w.i32(value.length);
      for (const v of value) w.i32(v);
      break;
    case TAG_LIST: {
      const elemType = value.length ? Array.isArray(value[0]) ? TAG_COMPOUND : TAG_INT : TAG_END;
      w.byte(elemType);
      w.i32(value.length);
      for (const item of value) {
        if (elemType === TAG_COMPOUND) writeCompound(w, item);
        else writeTagValue(w, elemType, item);
      }
      break;
    }
    case TAG_COMPOUND:
      writeCompound(w, value);
      break;
    default:
      throw new Error(`NBT: \u4E0D\u652F\u6301\u5199 tag \u7C7B\u578B ${type}`);
  }
}
function writeCompound(w, obj) {
  for (const key of Object.keys(obj)) {
    const value = obj[key];
    if (value && typeof value === "object" && value._short) {
      w.byte(TAG_SHORT);
      w.string(key);
      w.i16(value.v);
      continue;
    }
    let type;
    if (typeof value === "number") {
      type = TAG_INT;
    } else if (typeof value === "string") {
      type = TAG_STRING;
    } else if (value instanceof Uint8Array) {
      type = TAG_BYTE_ARRAY;
    } else if (Array.isArray(value)) {
      type = TAG_LIST;
    } else if (value && typeof value === "object") {
      type = TAG_COMPOUND;
    } else {
      throw new Error("NBT: \u65E0\u6CD5\u63A8\u65AD tag \u7C7B\u578B");
    }
    writeNamedTag(w, type, key, value);
  }
  w.byte(TAG_END);
}
function buildNbt(rootCompound) {
  const w = new Writer();
  writeNamedTag(w, TAG_COMPOUND, "", rootCompound);
  return w.bytes();
}
function writeVarint(w, value) {
  let v = value >>> 0;
  while (v >= 128) {
    w.byte(v & 127 | 128);
    v >>>= 7;
  }
  w.byte(v);
}
function readVarint(r) {
  let result = 0;
  let shift = 0;
  for (; ; ) {
    const b = r.byte();
    result |= (b & 127) << shift;
    if ((b & 128) === 0) break;
    shift += 7;
    if (shift > 35) throw new Error("varint \u6EA2\u51FA");
  }
  return result >>> 0;
}
var BLOCK_COLORS = {
  "minecraft:air": [0, 0, 0, 0],
  "minecraft:stone": [127, 127, 127, 255],
  "minecraft:granite": [156, 124, 104, 255],
  "minecraft:diorite": [207, 207, 207, 255],
  "minecraft:andesite": [136, 136, 136, 255],
  "minecraft:grass_block": [95, 159, 53, 255],
  "minecraft:dirt": [134, 96, 67, 255],
  "minecraft:coarse_dirt": [134, 96, 67, 255],
  "minecraft:podzol": [78, 54, 36, 255],
  "minecraft:cobblestone": [123, 123, 123, 255],
  "minecraft:oak_planks": [180, 138, 83, 255],
  "minecraft:spruce_planks": [123, 87, 51, 255],
  "minecraft:birch_planks": [195, 177, 125, 255],
  "minecraft:jungle_planks": [156, 114, 71, 255],
  "minecraft:acacia_planks": [170, 107, 66, 255],
  "minecraft:dark_oak_planks": [75, 52, 31, 255],
  "minecraft:oak_log": [130, 102, 65, 255],
  "minecraft:spruce_log": [97, 71, 45, 255],
  "minecraft:birch_log": [197, 182, 140, 255],
  "minecraft:oak_leaves": [54, 114, 45, 255],
  "minecraft:spruce_leaves": [42, 86, 36, 255],
  "minecraft:birch_leaves": [92, 142, 66, 255],
  "minecraft:glass": [200, 220, 230, 90],
  "minecraft:white_concrete": [207, 213, 214, 255],
  "minecraft:black_concrete": [20, 22, 24, 255],
  "minecraft:red_concrete": [151, 42, 43, 255],
  "minecraft:green_concrete": [94, 145, 62, 255],
  "minecraft:blue_concrete": [59, 83, 165, 255],
  "minecraft:yellow_concrete": [193, 174, 47, 255],
  "minecraft:water": [54, 106, 206, 170],
  "minecraft:lava": [226, 96, 26, 255],
  "minecraft:sand": [219, 205, 152, 255],
  "minecraft:gravel": [132, 127, 122, 255],
  "minecraft:gold_block": [225, 196, 66, 255],
  "minecraft:iron_block": [214, 214, 214, 255],
  "minecraft:diamond_block": [88, 224, 212, 255],
  "minecraft:emerald_block": [66, 190, 110, 255],
  "minecraft:redstone_block": [173, 38, 38, 255],
  "minecraft:coal_block": [26, 26, 26, 255],
  "minecraft:netherrack": [110, 52, 52, 255],
  "minecraft:soul_sand": [131, 110, 96, 255],
  "minecraft:obsidian": [26, 11, 43, 255],
  "minecraft:bedrock": [83, 83, 83, 255],
  "minecraft:ice": [130, 200, 230, 150],
  "minecraft:snow_block": [240, 244, 245, 255],
  "minecraft:brick": [156, 92, 73, 255],
  "minecraft:bookshelf": [134, 96, 67, 255],
  "minecraft:mossy_cobblestone": [106, 119, 89, 255],
  "minecraft:clay": [183, 165, 146, 255],
  "minecraft:terracotta": [168, 90, 58, 255],
  "minecraft:white_wool": [233, 233, 233, 255],
  "minecraft:red_wool": [189, 66, 66, 255],
  "minecraft:green_wool": [103, 157, 76, 255],
  "minecraft:blue_wool": [88, 118, 198, 255],
  "minecraft:yellow_wool": [215, 192, 67, 255],
  "minecraft:black_wool": [33, 33, 33, 255],
  "minecraft:tnt": [196, 90, 60, 255],
  "minecraft:glowstone": [224, 209, 122, 255],
  "minecraft:sea_lantern": [185, 219, 188, 255],
  "minecraft:end_stone": [215, 207, 149, 255],
  "minecraft:purpur_block": [165, 127, 174, 255],
  "minecraft:prismarine": [98, 150, 143, 255]
};
function hashStringToColor(name) {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = h * 31 + name.charCodeAt(i) | 0;
  const r = h & 255;
  const g = h >> 8 & 255;
  const b = h >> 16 & 255;
  return [r, g, b, 255];
}
function blockColor(blockName) {
  if (Object.prototype.hasOwnProperty.call(BLOCK_COLORS, blockName)) {
    return BLOCK_COLORS[blockName].slice();
  }
  return hashStringToColor(blockName);
}
var SCHEM_VERSION_2 = 2;
async function parseSchematic(input) {
  const raw = input instanceof Uint8Array ? input : new Uint8Array(input);
  const decompressed = await gzipInflate(raw);
  const root = parseNbt(decompressed);
  const version = Number(root.Version ?? SCHEM_VERSION_2);
  const dataVersion = Number(root.DataVersion ?? 0);
  const width = Number(root.Width);
  const height = Number(root.Height);
  const length = Number(root.Length);
  if (!width || !height || !length) throw new Error("Schematic: \u7F3A\u5C11 Width/Height/Length");
  const paletteCompound = root.Palette;
  if (!paletteCompound) throw new Error("Schematic: \u7F3A\u5C11 Palette");
  const blockNames = [];
  for (const name of Object.keys(paletteCompound)) blockNames.push(name);
  blockNames.sort((a, b) => Number(paletteCompound[a]) - Number(paletteCompound[b]));
  const blockData = root.BlockData;
  if (!blockData) throw new Error("Schematic: \u7F3A\u5C11 BlockData");
  const blockToCi = /* @__PURE__ */ new Map();
  let nextCi = 1;
  const paletteRgba = Array.from({ length: 256 }, () => [0, 0, 0, 0]);
  const r = new Reader(blockData);
  const total = width * height * length;
  const voxels = [];
  for (let y = 0; y < height; y++) {
    for (let z = 0; z < length; z++) {
      for (let x = 0; x < width; x++) {
        const idx = readVarint(r);
        const name = blockNames[idx];
        if (name === airName || !name) continue;
        let ci = blockToCi.get(name);
        if (ci === void 0) {
          if (nextCi > 255) throw new Error("Schematic: \u8C03\u8272\u677F\u8D85\u51FA 256 \u8272\u4E0A\u9650 (Minecraft block \u79CD\u7C7B\u8FC7\u591A)");
          ci = nextCi++;
          blockToCi.set(name, ci);
          paletteRgba[ci] = blockColor(name);
        }
        voxels.push({ x, y, z, i: ci });
      }
    }
  }
  const offset = Array.isArray(root.Offset) ? root.Offset.slice(0, 3).map(Number) : [0, 0, 0];
  return {
    version,
    dataVersion,
    models: [{ size: [width, height, length], voxels }],
    palette: paletteRgba,
    scene: [{ modelIndex: 0, translation: [0, 0, 0], rotation: 0, hidden: false, name: "schematic" }],
    materials: {},
    frameCount: 1,
    schematic: {
      width,
      height,
      length,
      offset,
      blockNames,
      source: "sponge-v2"
    }
  };
}
async function voxelToSchematic(vox, opts = {}) {
  const fallbackBlock = opts.fallbackBlock || "minecraft:stone";
  let voxels = [];
  let palette = null;
  if (vox && Array.isArray(vox.models) && Array.isArray(vox.scene)) {
    const models = vox.models;
    for (const s of vox.scene) {
      const m = models[s.modelIndex];
      if (!m) continue;
      const [tx, ty, tz] = s.translation || [0, 0, 0];
      for (const v of m.voxels) {
        voxels.push({ x: v.x + tx, y: v.y + ty, z: v.z + tz, i: v.i });
      }
    }
    palette = vox.palette;
  } else if (vox && vox.model) {
    voxels = vox.model.voxels.map((v) => ({ ...v }));
    palette = vox.palette;
  } else if (vox && Array.isArray(vox.instances)) {
    for (const inst of vox.instances) {
      const [tx, ty, tz] = inst.translation || [0, 0, 0];
      for (const v of inst.voxels) voxels.push({ x: v.x + tx, y: v.y + ty, z: v.z + tz, i: v.i });
    }
    palette = vox.palette || null;
  } else if (vox && vox.voxels) {
    voxels = vox.voxels.map((v) => ({ ...v }));
    palette = vox.palette || null;
  }
  if (!voxels.length) throw new Error("voxelToSchematic: \u6CA1\u6709\u4F53\u7D20\u53EF\u5199");
  let sx = 0, sy = 0, sz = 0;
  for (const v of voxels) {
    if (v.x + 1 > sx) sx = v.x + 1;
    if (v.y + 1 > sy) sy = v.y + 1;
    if (v.z + 1 > sz) sz = v.z + 1;
  }
  const ciToBlock = new Array(256).fill(fallbackBlock);
  if (palette) {
    for (let ci = 1; ci < 256; ci++) {
      const col = palette[ci];
      if (!col) continue;
      ciToBlock[ci] = nearestBlockName(col);
    }
  }
  const usedBlocks = /* @__PURE__ */ new Set();
  for (const v of voxels) {
    const b = v.i === 0 ? airName : ciToBlock[v.i] || fallbackBlock;
    usedBlocks.add(b);
  }
  usedBlocks.add(airName);
  const blockList = Array.from(usedBlocks);
  blockList.sort((a, b) => a === airName ? -1 : b === airName ? 1 : 0);
  const blockToIndex = /* @__PURE__ */ new Map();
  blockList.forEach((b, i) => blockToIndex.set(b, i));
  const total = sx * sy * sz;
  const indexAt = new Int32Array(total).fill(blockToIndex.get(airName));
  for (const v of voxels) {
    const idx = v.x + v.z * sx + v.y * sx * sz;
    const b = v.i === 0 ? airName : ciToBlock[v.i] || fallbackBlock;
    indexAt[idx] = blockToIndex.get(b);
  }
  const w = new Writer();
  for (let k = 0; k < total; k++) writeVarint(w, indexAt[k]);
  const blockDataBytes = w.bytes();
  const paletteCompound = {};
  blockList.forEach((b, i) => {
    paletteCompound[b] = i;
  });
  const rootCompound = {
    Version: SCHEM_VERSION_2,
    DataVersion: 0,
    Width: { _short: true, v: sx },
    Height: { _short: true, v: sy },
    Length: { _short: true, v: sz },
    Offset: [0, 0, 0],
    Palette: paletteCompound,
    BlockData: blockDataBytes
  };
  const nbt = buildNbt(rootCompound);
  return gzipDeflate(nbt);
}
function nearestBlockName(rgba) {
  let best = null;
  let bestDist = Infinity;
  for (const name of Object.keys(BLOCK_COLORS)) {
    if (name === airName) continue;
    const c = BLOCK_COLORS[name];
    const d = (c[0] - rgba[0]) ** 2 + (c[1] - rgba[1]) ** 2 + (c[2] - rgba[2]) ** 2;
    if (d < bestDist) {
      bestDist = d;
      best = name;
    }
  }
  return best || "minecraft:stone";
}

// src/symmetry.js
function mirrorCoordinates(x, y, z, size, symmetry = {}) {
  const [sx, sy, sz] = size;
  const axes = [];
  if (symmetry.x) axes.push("x");
  if (symmetry.y) axes.push("y");
  if (symmetry.z) axes.push("z");
  const seen = /* @__PURE__ */ new Set();
  const out = [];
  const push = (X, Y, Z) => {
    const k = `${X},${Y},${Z}`;
    if (seen.has(k)) return;
    seen.add(k);
    out.push([X, Y, Z]);
  };
  push(x, y, z);
  const n = axes.length;
  for (let mask = 1; mask < 1 << n; mask++) {
    let X = x;
    let Y = y;
    let Z = z;
    for (let i = 0; i < n; i++) {
      if (mask & 1 << i) {
        const a = axes[i];
        if (a === "x") X = sx - 1 - X;
        else if (a === "y") Y = sy - 1 - Y;
        else Z = sz - 1 - Z;
      }
    }
    push(X, Y, Z);
  }
  return out;
}

// src/voxelize.js
function dot(a, b) {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}
function sub(a, b) {
  return [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
}
function cross(a, b) {
  return [
    a[1] * b[2] - a[2] * b[1],
    a[2] * b[0] - a[0] * b[2],
    a[0] * b[1] - a[1] * b[0]
  ];
}
function triBoxOverlap(c, h, v0, v1, v2) {
  const f0 = sub(v0, c);
  const f1 = sub(v1, c);
  const f2 = sub(v2, c);
  for (let axis = 0; axis < 3; axis++) {
    const p0 = f0[axis], p1 = f1[axis], p2 = f2[axis];
    const mn = Math.min(p0, p1, p2);
    const mx = Math.max(p0, p1, p2);
    if (mn > h[axis] || mx < -h[axis]) return false;
  }
  const e0 = sub(f1, f0);
  const e1 = sub(f2, f1);
  const e2 = sub(f0, f2);
  const n = cross(e0, e1);
  const axes = [
    n,
    cross([1, 0, 0], e0),
    cross([1, 0, 0], e1),
    cross([1, 0, 0], e2),
    cross([0, 1, 0], e0),
    cross([0, 1, 0], e1),
    cross([0, 1, 0], e2),
    cross([0, 0, 1], e0),
    cross([0, 0, 1], e1),
    cross([0, 0, 1], e2)
  ];
  for (const a of axes) {
    if (Math.abs(a[0]) + Math.abs(a[1]) + Math.abs(a[2]) < 1e-12) continue;
    const ra = h[0] * Math.abs(a[0]) + h[1] * Math.abs(a[1]) + h[2] * Math.abs(a[2]);
    const pa = Math.min(dot(f0, a), dot(f1, a), dot(f2, a));
    const pb = Math.max(dot(f0, a), dot(f1, a), dot(f2, a));
    if (pa > ra || pb < -ra) return false;
  }
  return true;
}
function rayTriHit(ox, oy, oz, dx, dy, dz, a, b, c) {
  const e1 = sub(b, a);
  const e2 = sub(c, a);
  const pvec = cross([dx, dy, dz], e2);
  const det = dot(e1, pvec);
  if (Math.abs(det) < 1e-12) return false;
  const inv = 1 / det;
  const tvec = [ox - a[0], oy - a[1], oz - a[2]];
  const u = dot(tvec, pvec) * inv;
  if (u < 0 || u > 1) return false;
  const qvec = cross(tvec, e1);
  const v = dot([dx, dy, dz], qvec) * inv;
  if (v < 0 || u + v > 1) return false;
  const t = dot(e2, qvec) * inv;
  return t > 1e-9;
}
function voxelizeMesh(triangles, options = {}) {
  const {
    resolution = 64,
    mode = "shell",
    pad = 0,
    color = [200, 205, 215, 255],
    bounds = null
  } = options;
  if (!Array.isArray(triangles) || triangles.length === 0) {
    throw new Error("voxelizeMesh: triangles \u4E0D\u80FD\u4E3A\u7A7A");
  }
  const mn = [Infinity, Infinity, Infinity];
  const mx = [-Infinity, -Infinity, -Infinity];
  if (bounds) {
    for (let i = 0; i < 3; i++) {
      mn[i] = bounds[0][i];
      mx[i] = bounds[1][i];
    }
  } else {
    for (const t of triangles) {
      for (const v of [t.a, t.b, t.c]) {
        for (let i = 0; i < 3; i++) {
          if (v[i] < mn[i]) mn[i] = v[i];
          if (v[i] > mx[i]) mx[i] = v[i];
        }
      }
    }
  }
  const span = [mx[0] - mn[0], mx[1] - mn[1], mx[2] - mn[2]];
  let size;
  if (Array.isArray(resolution)) {
    size = [
      Math.max(1, Math.round(resolution[0])),
      Math.max(1, Math.round(resolution[1])),
      Math.max(1, Math.round(resolution[2]))
    ];
  } else {
    const r = Math.max(1, Math.round(resolution));
    let longest = 0;
    for (let i = 0; i < 3; i++) if (span[i] > span[longest]) longest = i;
    size = [1, 1, 1];
    for (let i = 0; i < 3; i++) {
      if (span[i] < 1e-9) {
        size[i] = 1;
        continue;
      }
      size[i] = Math.max(1, Math.round(span[i] / span[longest] * r));
    }
  }
  const vs = [
    span[0] < 1e-9 ? 1 : span[0] / size[0],
    span[1] < 1e-9 ? 1 : span[1] / size[1],
    span[2] < 1e-9 ? 1 : span[2] / size[2]
  ];
  const fpad = Math.max(0, Math.floor(pad));
  const fsize = [size[0] + 2 * fpad, size[1] + 2 * fpad, size[2] + 2 * fpad];
  const origin = [mn[0] - fpad * vs[0], mn[1] - fpad * vs[1], mn[2] - fpad * vs[2]];
  const palette = defaultPalette();
  const colorToCi = /* @__PURE__ */ new Map();
  let nextCi = 1;
  const ciFor = (col) => {
    const c = Array.isArray(col) && col.length >= 3 ? [col[0] | 0, col[1] | 0, col[2] | 0, col[3] === void 0 ? 255 : col[3] | 0] : color;
    const key = c.join(",");
    const hit = colorToCi.get(key);
    if (hit !== void 0) return hit;
    let ci;
    if (nextCi <= 255) {
      ci = nextCi++;
    } else {
      ci = 1;
      let best = Infinity;
      for (const [, v] of colorToCi) {
        const p = palette[v];
        const d = (p[0] - c[0]) ** 2 + (p[1] - c[1]) ** 2 + (p[2] - c[2]) ** 2;
        if (d < best) {
          best = d;
          ci = v;
        }
      }
    }
    colorToCi.set(key, ci);
    palette[ci] = c;
    return ci;
  };
  const grid = new VoxelGrid(fsize[0], fsize[1], fsize[2]);
  const clampIdx = (val, max) => {
    const i = Math.floor(val);
    return i < 0 ? 0 : i > max ? max : i;
  };
  if (mode === "solid") {
    const ej = 1e-3 * vs[1];
    const ek = 2e-3 * vs[2];
    for (let i = 0; i < fsize[0]; i++) {
      const cx = origin[0] + (i + 0.5) * vs[0];
      for (let j = 0; j < fsize[1]; j++) {
        const cy = origin[1] + (j + 0.5) * vs[1] + ej;
        for (let k = 0; k < fsize[2]; k++) {
          const cz = origin[2] + (k + 0.5) * vs[2] + ek;
          let count = 0;
          let col = null;
          for (const t of triangles) {
            if (rayTriHit(cx, cy, cz, 1, 0, 0, t.a, t.b, t.c)) {
              count++;
              if (col === null) col = t.color || color;
            }
          }
          if (count & 1) grid.voxels.set(`${i},${j},${k}`, ciFor(col === null ? color : col));
        }
      }
    }
  } else {
    const half = [vs[0] * 0.5, vs[1] * 0.5, vs[2] * 0.5];
    for (const t of triangles) {
      const tmn = [
        Math.min(t.a[0], t.b[0], t.c[0]),
        Math.min(t.a[1], t.b[1], t.c[1]),
        Math.min(t.a[2], t.b[2], t.c[2])
      ];
      const tmx = [
        Math.max(t.a[0], t.b[0], t.c[0]),
        Math.max(t.a[1], t.b[1], t.c[1]),
        Math.max(t.a[2], t.b[2], t.c[2])
      ];
      const EPS = 1e-6;
      const ix0 = clampIdx((tmn[0] - EPS - origin[0]) / vs[0], fsize[0] - 1);
      const ix1 = clampIdx((tmx[0] + EPS - origin[0]) / vs[0], fsize[0] - 1);
      const iy0 = clampIdx((tmn[1] - EPS - origin[1]) / vs[1], fsize[1] - 1);
      const iy1 = clampIdx((tmx[1] + EPS - origin[1]) / vs[1], fsize[1] - 1);
      const iz0 = clampIdx((tmn[2] - EPS - origin[2]) / vs[2], fsize[2] - 1);
      const iz1 = clampIdx((tmx[2] + EPS - origin[2]) / vs[2], fsize[2] - 1);
      const ci = ciFor(t.color || color);
      for (let i = ix0; i <= ix1; i++) {
        if (i < fpad || i >= size[0] + fpad) continue;
        const cx = origin[0] + (i + 0.5) * vs[0];
        for (let j = iy0; j <= iy1; j++) {
          if (j < fpad || j >= size[1] + fpad) continue;
          const cy = origin[1] + (j + 0.5) * vs[1];
          for (let k = iz0; k <= iz1; k++) {
            if (k < fpad || k >= size[2] + fpad) continue;
            const cz = origin[2] + (k + 0.5) * vs[2];
            const key = `${i},${j},${k}`;
            if (grid.voxels.has(key)) continue;
            if (triBoxOverlap([cx, cy, cz], half, t.a, t.b, t.c)) {
              grid.voxels.set(key, ci);
            }
          }
        }
      }
    }
  }
  return { grid, palette };
}

// src/csg.js
var CSG_OP = (
  /** @type {const} */
  {
    UNION: "union",
    INTERSECTION: "intersection",
    DIFFERENCE: "difference"
  }
);
function voxelCSG(A, B, op, options = {}) {
  if (!(A instanceof VoxelGrid) || !(B instanceof VoxelGrid)) {
    throw new TypeError("voxelCSG: A / B \u5FC5\u987B\u662F VoxelGrid \u5B9E\u4F8B");
  }
  if (!Object.values(CSG_OP).includes(op)) {
    throw new Error(`voxelCSG: \u672A\u77E5\u8FD0\u7B97 "${op}" (\u5E94\u4E3A union|intersection|difference)`);
  }
  const tie = options.colorTie === "b" ? "b" : "a";
  const sx = Math.max(A.sx, B.sx);
  const sy = Math.max(A.sy, B.sy);
  const sz = Math.max(A.sz, B.sz);
  const out = new VoxelGrid(sx, sy, sz);
  if (op === CSG_OP.UNION) {
    for (const [k, ci] of A.voxels) out.voxels.set(k, ci);
    for (const [k, ci] of B.voxels) if (!out.voxels.has(k)) out.voxels.set(k, ci);
  } else if (op === CSG_OP.INTERSECTION) {
    const [primary, secondary] = tie === "b" ? [B, A] : [A, B];
    for (const [k, ci] of primary.voxels) {
      if (secondary.voxels.has(k)) out.voxels.set(k, ci);
    }
  } else {
    for (const [k, ci] of A.voxels) {
      if (!B.voxels.has(k)) out.voxels.set(k, ci);
    }
  }
  return out;
}
function gridFromMap(map, size) {
  const g = new VoxelGrid(size[0], size[1], size[2]);
  for (const [k, ci] of map) g.voxels.set(k, ci);
  return g;
}
export {
  CSG_OP,
  MAGIC,
  ROTATION_MATRICES,
  VERSION,
  VoxelGrid,
  blockColor,
  defaultPalette,
  downloadVox,
  gridFromMap,
  hsvToRgb,
  mirrorCoordinates,
  parseSchematic,
  parseVox,
  rainbowPalette,
  toVoxBytes,
  toVoxBytesScene,
  voxelCSG,
  voxelToSchematic,
  voxelizeMesh
};
